import { Button, TableItem } from 'components/atoms';
import { Table, TopBar } from 'components/organisms';
import { TopbarPage } from 'components/templates';
import { useTableColumnsVisibility } from 'hooks/useTableColumnsVisibility';
import { resources } from 'locales/i18n';
import { useMemo } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { PiMagicWand, PiWarningOctagon } from 'react-icons/pi';
import { useGetSupportedLanguages } from 'services/enums';
import { Column } from 'types/components/table/table.types';

enum SORT_COLUMNS {
  id,
  en,
  pl,
}

const SupportedLanguages = () => {
  const { t } = useTranslation();

  const columnsVisibility = useTableColumnsVisibility(
    'SupportedLanguagesDevTools',
    SORT_COLUMNS
  );

  const supportedLanguages = useGetSupportedLanguages();

  const languageNodes = useMemo(
    () =>
      supportedLanguages.data?.map(e => ({
        id: `${e.language}`,
      })) ?? [],
    [supportedLanguages.data]
  );

  const appSupportedLanguages = useMemo<string[]>(
    () => Object.keys(resources),
    [resources]
  );

  const onClickToCopy = (title: string, value: string) => {
    toast.success(`${title} ${value} has been copied to clipboard!`);
    navigator.clipboard.writeText(value);
  };

  const getTranslation = (lng: string, id: string) =>
    t(`enum.supportedLanguages.${id}`, { lng: lng });

  const getMissingSupportedLanguages = () => {
    const missingSupportedLanguagesByLanguage = appSupportedLanguages.map(
      l => ({
        [l]: languageNodes
          .filter(e =>
            getTranslation(l, `${e.id}`).includes('enum.supportedLanguages.')
          )
          .map(e => e.id),
      })
    );

    console.log(
      'Missing supportedLanguages translations:',
      missingSupportedLanguagesByLanguage
    );
    toast.success(`Check the console for result`);
  };

  return (
    <TopbarPage
      topbar={
        <TopBar
          title={`Supported languages list (${
            supportedLanguages.data?.length ?? 0
          })`}
          hideBackButton
          topbarContent={
            <>
              <Button
                disabled={supportedLanguages.isLoading}
                isClear
                onlyIcon
                onClick={getMissingSupportedLanguages}>
                <Button.Description description={t('button.generate')} />

                <Button.Icon icon={<PiMagicWand />} />
              </Button>
              <Table.CustomColumnsButton
                columns={SORT_COLUMNS}
                isClear
                visibility={columnsVisibility}
                i18nextPrefix=' '
                customI18next=' '
              />
            </>
          }
        />
      }>
      <Table
        id={columnsVisibility.tableId}
        loading={supportedLanguages.isLoading}
        size='compact'
        // displayMode
        data={{
          nodes: languageNodes,
        }}
        columns={[
          {
            label: 'Id',
            renderCell: ({ id }) => (
              <TableItem
                className='cursor-pointer'
                onClick={() => onClickToCopy('id', id)}>
                <TableItem.Text text={`${id}`} />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(SORT_COLUMNS.id),
          },
          ...appSupportedLanguages.map<Column<{ id: string }>>((l, key) => ({
            label: l.toUpperCase(),
            renderCell: ({ id }) => (
              <TableItem>
                {getTranslation(l, id).includes('enum.supportedLanguages.') ? (
                  <TableItem.Label color='red' size='compact'>
                    <PiWarningOctagon /> NOT FOUND
                  </TableItem.Label>
                ) : (
                  <TableItem.Text text={getTranslation(l, id)} />
                )}
              </TableItem>
            ),
            resize: key !== appSupportedLanguages.length - 1,
            hide: columnsVisibility.hide(
              SORT_COLUMNS[l as keyof typeof SORT_COLUMNS]
            ),
          })),
        ]}
      />
    </TopbarPage>
  );
};

export default SupportedLanguages;
